import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { Frame } from '../store/types';
import { resolveTsxRender } from '../store/resolve-tsx';
import { useTsxModule } from './useTsxModule';
import { effectToCssFilter } from '../effects/css-filters';
import { applyTheme } from './applyTheme';
import { useCanvasStore } from '../store/canvas-store';
import { TsxErrorBoundary } from './TsxErrorBoundary';
import { stampDomCfIds } from './stampDomCfIds';
import { useFrameMotion } from '../motion/useFrameMotion';
import { useFrameLottie } from '../lottie/useFrameLottie';

interface Props { frame: Frame; byId: Map<string, Frame> }

export function TsxRenderer({ frame, byId }: Props) {
  const resolved = useMemo(() => resolveTsxRender(frame, byId), [frame, byId]);
  const tsx = 'tsx' in resolved ? resolved.tsx : '';
  const mod = useTsxModule(tsx);

  const ref = useRef<HTMLDivElement>(null);
  const activeThemeId = useCanvasStore((s) => s.doc.activeThemeId);
  useEffect(() => {
    if (!ref.current) return;
    applyTheme(ref.current, frame, byId, activeThemeId);
  }, [frame, byId, activeThemeId]);

  // Stamp data-cf-id onto the rendered React DOM so motion (and the node
  // picker) can target it — React output carries none. Layout effect runs
  // before useFrameMotion's effect, so cf-ids exist when the player first seeks.
  // Re-stamps when the source / props change (structure may differ).
  useLayoutEffect(() => {
    if (ref.current) stampDomCfIds(ref.current);
  }, [tsx, mod.status, JSON.stringify('props' in resolved ? resolved.props : null)]);

  // Mount the motion player (no-op without a timeline). tsxInstance inherits its
  // tsxMain's timeline via useFrameMotion; the resolved DOM shares cf-ids.
  useFrameMotion(ref, frame);
  // Hydrate [data-lottie] elements stamped into the rendered React DOM (no-op without any).
  useFrameLottie(ref, frame);

  let body: React.ReactNode;
  if ('error' in resolved) {
    body = <div className="cf-missing" data-cf-error="missing">{resolved.error}</div>;
  } else if (mod.status === 'error') {
    body = <div className="cf-missing" data-cf-error="compile">{mod.message}</div>;
  } else if (mod.status === 'loading') {
    body = null;
  } else {
    const Component = mod.Component;
    // key by source so editing the tsx remounts a fresh boundary (clears the
    // error); propsKey clears a captured error on prop change without remount.
    body = (
      <TsxErrorBoundary key={tsx} propsKey={JSON.stringify(resolved.props)}>
        <Component {...resolved.props} />
      </TsxErrorBoundary>
    );
  }

  return (
    <div
      ref={ref}
      className="codeframe-content"
      style={{ width: '100%', height: '100%', overflow: 'auto', contain: 'layout', filter: effectToCssFilter(frame.effect) }}
    >
      {body}
    </div>
  );
}
